import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { STANDARD_LANGUAGES, applyUserLanguage, fetchLanguageCatalogue } from '../../utilities/languageCatalogue'

/**
 * Historia 138 — resolves, loads and applies the language of the signed-in user, and tells the
 * caller when that is done.
 *
 * ⚠️ **Why this reports readiness instead of just doing its work.** The customer's words have to be
 * there on the FIRST paint. Today there is already one flicker, English → the user's language, and
 * what the rules forbid is ADDING a second one, standard words → the customer's. The only way to
 * avoid it is to hold the authenticated tree until the dictionary is registered and the language
 * applied, which is what `ready` is for.
 *
 * ⚠️ **It can never get stuck.** Both steps swallow their own failures by design: a catalogue that
 * cannot be read degrades to the eight that ship with the application, and a dictionary that cannot
 * be read leaves the interface on the base language. Both reads are bounded in time. And `ready` is
 * set in a `finally`, so even something throwing halfway leaves the user on a language rather than
 * on a spinner: degrading is always better than hanging.
 *
 * ⚠️ **`ready` is only ever withheld while there is no user -- never because the effect re-ran.**
 * On 2026-09-18 the application stayed on the loading indicator forever after a reload: the
 * opening `settle()` was in flight (a replacing language means one dictionary download) when the
 * `user` object was renewed by the context -- same person, same code, new identity. The effect's
 * cleanup switched off a `subscribed` flag, the re-run saw the session already opened and started
 * nothing, and the settle in flight finished unable to report. So the guard here is "still
 * mounted", tracked by a ref that only the unmount touches, not "dependencies unchanged".
 *
 * The catalogue is read ONCE per session and handed back for the selector to draw itself from. The
 * two-moments rule of this story governs the CONTENT route, not this one. The one thing that changes
 * it within a session is a registration in the Backoffice, which hands over the catalogue it has just
 * read through `adoptCatalogue` -- so the new language reaches the selector without a reload and
 * without a second request.
 *
 * @param {Object} user The signed-in user, or a falsy value while there is none
 * @returns {{ready: boolean, catalogue: Array<Object>, changeLanguage: function(string): Promise<void>,
 *   adoptCatalogue: function(Array<Object>): void}}
 *   Whether the language is settled, the catalogue, the way to switch language, and the way to
 *   replace the session's catalogue with a fresher one
 */
const useUserLanguage = (user) => {
  const { i18n } = useTranslation()
  const [ready, setReady] = useState(false)
  const [catalogue, setCatalogue] = useState(STANDARD_LANGUAGES)
  // The catalogue read is per session, so it must not run again when the language changes.
  const catalogueRef = useRef(null)

  const selectedCode = user?.userConfiguration?.languageCode
  // The opening move runs ONCE per session. Afterwards the selector drives every change by calling
  // `changeLanguage` itself -- which matters because re-picking the language already in use has to
  // fetch the dictionary again (that is what makes a hand edit of the file visible), and an effect
  // watching the saved code would not fire when the code does not change. Letting both drive it
  // would fetch twice on every change.
  const openedRef = useRef(false)
  // Whether this hook is still mounted. It is the ONLY thing that may stop a finished resolution
  // from reporting -- see the note at the top about the reload that never finished.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /**
   * Applies a chosen code, reading the catalogue first if this session has not read it yet.
   *
   * @param {string} code The code the user chose
   * @returns {Promise<void>} Resolves once the interface is running on the right language
   */
  const changeLanguage = useCallback(async (code) => {
    if (catalogueRef.current === null) {
      catalogueRef.current = await fetchLanguageCatalogue()
      setCatalogue(catalogueRef.current)
    }
    await applyUserLanguage(i18n, code, catalogueRef.current)
  }, [i18n])

  /**
   * Replaces the session's catalogue with one somebody has just read.
   *
   * ⚠️ The ref has to change as well as the state: the state draws the selector, but `changeLanguage`
   * resolves with the ref, and a language that replaces its base would otherwise not be loaded when
   * that base is picked.
   *
   * @param {Array<Object>} nextCatalogue The catalogue as `fetchLanguageCatalogue` returns it
   */
  const adoptCatalogue = useCallback((nextCatalogue) => {
    catalogueRef.current = nextCatalogue
    setCatalogue(nextCatalogue)
  }, [])

  useEffect(() => {
    // No user means the login screen, and the login screen asks for nothing: neither the catalogue
    // nor any content. That is what lets the content be served authenticated, with no route open in
    // the clear.
    if (!user) {
      setReady(false)
      catalogueRef.current = null
      openedRef.current = false
      return
    }

    if (openedRef.current) {
      return
    }
    openedRef.current = true

    const settle = async () => {
      try {
        await changeLanguage(selectedCode)
      } catch (error) {
        // The interface is on whatever language i18next was left with -- English at worst -- and
        // that is still better than a spinner nobody can get past. The cause belongs in the console.
        console.error('The user language could not be resolved; carrying on with the current one', error)
      } finally {
        if (mountedRef.current) {
          setReady(true)
        }
      }
    }
    settle()
  }, [user, selectedCode, changeLanguage])

  return { ready, catalogue, changeLanguage, adoptCatalogue }
}

export default useUserLanguage
